import { Box } from '@mui/material';
import { compact } from 'lodash';

import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { ApplicationConfigurationFormFields } from '@/pages/admin/ApplicationConfigurationForm/ApplicationConfigurationFormFields';
import {
  AugmentedUpdateOrganizationApplicationConfigurationInput,
  McpAccess,
  OrganizationApplicationConfigurationMaxSessionDuration,
  OrganizationApplicationConfigurationUserSessionInactivityTimeout,
} from '@/types/schema';

import { useUpdateOrganizationConfigurationFormMutation } from './graphql/OrganizationConfigurationForm.generated';
import { useOrganizationConfigurationFormDefaultValues } from './hooks/useOrganizationConfigurationFormDefaultValues';
import { OrganizationConfigurationFormShape } from './OrganizationConfigurationForm.constants';

interface OrganizationConfigurationFormProps {
  luminaryOrganizationID: string;
  readOnly?: boolean;
}

export function OrganizationConfigurationForm({
  luminaryOrganizationID,
  readOnly = false,
}: OrganizationConfigurationFormProps) {
  const formMethods = useForm<OrganizationConfigurationFormShape>({
    defaultValues: {
      collaboratorDomainAllowList: [],
      disableAdvancedAnalytics: false,
      disableAiCapabilities: false,
      disableAiInsights: false,
      disableRoleManagement: false,
      disclaimerConfigurationOverridesDescendants: false,
      enableEmployeeIdpId: false,
      mcpAccess: McpAccess.Off,
      smartReviewEnabled: true,
      maxSessionDuration:
        OrganizationApplicationConfigurationMaxSessionDuration.TwentyEightDays,
      userSessionInactivityTimeout:
        OrganizationApplicationConfigurationUserSessionInactivityTimeout.Off,
    },
  });
  const { createSuccessFeedback, showFeedback } = useFeedback();
  const { data: initialData } = useOrganizationConfigurationFormDefaultValues(
    luminaryOrganizationID,
    formMethods.reset
  );
  const [updateOrgConfiguration, { loading }] =
    useUpdateOrganizationConfigurationFormMutation({
      onCompleted: createSuccessFeedback(
        'Successfully updated organization configuration'
      ),
      onError: (error) => {
        const userFacingMessages = getUserFacingErrorMessages(error);
        if (userFacingMessages.length > 0) {
          showFeedback(userFacingMessages.join('. '));
        } else {
          showFeedback(
            'There was an error updating the organization configuration. Please try again.'
          );
        }
      },
    });

  const onSubmit = formMethods.handleSubmit((formValues) => {
    const config =
      initialData?.organizationApplicationConfigurations.edges?.[0]?.node;
    const configId = config?.id;
    if (!configId) {
      showFeedback(
        'No organization configuration found. Please ensure the organization has been fully provisioned.'
      );
      return;
    }

    const domainValues = compact(
      formValues.collaboratorDomainAllowList.map((d) => d.value.trim())
    );

    const input: AugmentedUpdateOrganizationApplicationConfigurationInput = {
      id: configId,
      update: {
        collaboratorDomainAllowList: domainValues,
        clearCollaboratorDomainAllowList: domainValues.length === 0,
        disableAdvancedAnalytics: formValues.disableAdvancedAnalytics,
        disableAiCapabilities: formValues.disableAiCapabilities,
        disableAiInsights: formValues.disableAiInsights,
        disableRoleManagement: formValues.disableRoleManagement,
        disclaimerConfigurationOverridesDescendants:
          formValues.disclaimerConfigurationOverridesDescendants,
        enableEmployeeIdpID: formValues.enableEmployeeIdpId,
        mcpAccess: formValues.mcpAccess,
        smartReviewEnabled: formValues.smartReviewEnabled,
        maxSessionDuration: formValues.maxSessionDuration,
        userSessionInactivityTimeout: formValues.userSessionInactivityTimeout,
      },
    };
    return updateOrgConfiguration({ variables: { input } });
  });

  return (
    <FormProvider {...formMethods}>
      <FormFieldsDisabledProvider isDisabled={readOnly}>
        <Box component="form" onSubmit={onSubmit}>
          <ApplicationConfigurationFormFields
            configurationLevel="organization"
            loading={loading}
            readOnly={readOnly}
          />
        </Box>
      </FormFieldsDisabledProvider>
    </FormProvider>
  );
}
